import React, { useState } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VendorAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30d');

  // Sales Performance Data
  const salesData = [
    { month: 'Jun', revenue: 145000, orders: 1250 },
    { month: 'Jul', revenue: 168000, orders: 1420 },
    { month: 'Aug', revenue: 192000, orders: 1680 },
    { month: 'Sep', revenue: 185000, orders: 1590 },
    { month: 'Oct', revenue: 210000, orders: 1850 },
    { month: 'Nov', revenue: 245000, orders: 2100 }
  ];

  // Category Distribution Data
  const categoryData = [
    { name: 'Skincare', value: 35, color: '#10b981' },
    { name: 'Makeup', value: 28, color: '#f59e0b' },
    { name: 'Hair Care', value: 22, color: '#3b82f6' },
    { name: 'Fragrance', value: 15, color: '#8b5cf6' }
  ];

  // Top Performing Vendors
  const topVendors = [
    { name: 'Hair Care Experts', revenue: '$215,680', growth: '+12%', orders: 2134 },
    { name: 'Beauty Essentials Co.', revenue: '$125,450', growth: '+8%', orders: 1250 },
    { name: 'Luxury Fragrance House', revenue: '$98,750', growth: '+15%', orders: 645 },
    { name: 'Glamour Makeup Studio', revenue: '$89,320', growth: '+6%', orders: 856 },
    { name: 'Organic Beauty Labs', revenue: '$42,150', growth: '-3%', orders: 423 }
  ];

  // Vendor Health Metrics
  const healthMetrics = [
    { label: 'Avg Fulfillment Rate', value: '96.2%', trend: '+2.1%', icon: 'TrendingUp', color: 'text-success' },
    { label: 'Avg Response Time', value: '3.5h', trend: '-0.5h', icon: 'Clock', color: 'text-info' },
    { label: 'Customer Satisfaction', value: '4.7/5', trend: '+0.2', icon: 'Star', color: 'text-warning' },
    { label: 'Product Return Rate', value: '2.3%', trend: '-0.4%', icon: 'RotateCcw', color: 'text-success' }
  ];

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={20} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Time Period:</span>
          </div>
          <div className="flex gap-2">
            {['7d', '30d', '90d', '1y']?.map((range) => (
              <Button
                key={range}
                variant={timeRange === range ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeRange(range)}
              >
                {range === '7d' && 'Last 7 Days'}
                {range === '30d' && 'Last 30 Days'}
                {range === '90d' && 'Last 90 Days'}
                {range === '1y' && 'Last Year'}
              </Button>
            ))}
          </div>
        </div>
      </div>
      {/* Health Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {healthMetrics?.map((metric, index) => (
          <div
            key={index}
            className="bg-card rounded-xl border border-border p-6 hover:shadow-elevation-2 transition-shadow"
          >
            <div className="flex items-center justify-between mb-2">
              <Icon name={metric?.icon} size={20} className="text-muted-foreground" />
              <span className={`text-sm font-medium ${metric?.color}`}>
                {metric?.trend}
              </span>
            </div>
            <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
            <p className="text-sm text-muted-foreground">{metric?.label}</p>
          </div>
        ))}
      </div>
      {/* Revenue and Orders Chart */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Revenue & Orders Trend
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Total vendor performance over time
            </p>
          </div>
          <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
        
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={salesData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="month" 
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              yAxisId="left"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="revenue" 
              stroke="#10b981" 
              strokeWidth={2}
              name="Revenue ($)"
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="orders" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Orders"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-6">
            Category Distribution
          </h3>
          
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name} (${value}%)`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {categoryData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Performing Vendors */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Top Performing Vendors
            </h3>
            <Button variant="ghost" size="sm" iconName="ExternalLink">
              View All
            </Button>
          </div>
          
          <div className="space-y-4">
            {topVendors?.map((vendor, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {vendor?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {vendor?.orders} orders
                    </p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-sm font-semibold text-foreground">
                    {vendor?.revenue}
                  </p>
                  <p className={`text-xs font-medium ${
                    vendor?.growth?.startsWith('+') ? 'text-success' : 'text-error'
                  }`}>
                    {vendor?.growth}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorAnalytics;